import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useMediaQuery,
  useTheme,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import { toast } from "react-toastify";

import { getUserPersonalInfo, resetPasswordAPI } from "../../../../api/userApi";
import { useChangePasswordStyles } from "./styles";
import { Eye, EyeOff } from "lucide-react";
import TwoFA from "../2FA";

const isStrongPassword = (password: string) => {
  return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/.test(
    password
  );
};

const ChangePassword: React.FC = () => {
  const classes = useChangePasswordStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const toggleVisibility = (field: "current" | "new" | "confirm") => {
    setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await getUserPersonalInfo();
        setEmail(res.data.email);
      } catch (err) {
        console.error("Failed to fetch email", err);
        toast.error("Unable to load user info.");
      }
    })();
  }, []);

  const handleSave = async () => {
    if (!password || !newPassword || !confirmPassword) {
      return toast.error("Please fill in all password fields");
    }

    if (!isStrongPassword(newPassword)) {
      return toast.error(
        "New password must be at least 8 characters long and include uppercase, lowercase, number, and special character"
      );
    }

    if (newPassword !== confirmPassword) {
      return toast.error("New password and confirm password do not match!");
    }

    setLoading(true);
    try {
      await resetPasswordAPI({
        email,
        password,
        newPassword,
        confirmPassword,
      });

      toast.success("Password updated successfully!");
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Reset password error:", error);
      toast.error(
        (error as any)?.response?.data?.message || "Failed to update password."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.section}>
      <Box className={classes.sectionContainer}>
        <Typography className={classes.sectionTitle}>Password and 2FA</Typography>

        <Box>
          <Typography className={classes.sectionSubTitle}>
            Change password
          </Typography>
          <Box className={classes.form}>
            {/* Current password field */}
            <Box className={classes.passwordField}>
              <Typography className={classes.passwordTitle}>
                Current Password
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                type={showPassword.current ? "text" : "password"}
                name="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className={classes.inputField}
                placeholder="Enter your Current password"
                InputProps={{
                  style: { fontSize: "16px" },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleVisibility("current")}>
                        {showPassword.current ? <EyeOff /> : <Eye />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* New password field */}
            <Box className={classes.passwordField} sx={{ paddingTop: 14 }}>
              <Typography className={classes.passwordTitle}>
                New Password
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                type={showPassword.new ? "text" : "password"}
                name="newPassword"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className={classes.inputField}
                placeholder="Create your password"
                InputProps={{
                  style: { fontSize: "16px" },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleVisibility("new")}>
                        {showPassword.new ? <EyeOff /> : <Eye />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                className={classes.infoText}
                style={{ fontSize: "13px", marginTop: "8px" }}
              >
                Must contain capital letter, 8 or more characters, number and a special symbol
              </Typography>
            </Box>

            {/* Confirm new password field */}
            <Box className={classes.passwordField}>
              <Typography className={classes.passwordTitle}>
                Confirm New Password
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                type={showPassword.confirm ? "text" : "password"}
                name="confirmPassword"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className={classes.inputField}
                placeholder="Enter your new password again"
                InputProps={{
                  style: { fontSize: "16px" },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => toggleVisibility("confirm")}>
                        {showPassword.confirm ? <EyeOff /> : <Eye />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Box className={classes.sectionFooter}>
            <Box className={classes.buttonContainer}>
              <>
                {/* <Button variant="contained" className={classes.cancelButton}>
                  Cancel
                </Button> */}
                <Button
                  variant="contained"
                  className={classes.saveButton}
                  onClick={handleSave}
                  disabled={loading}
                >
                  Save changes
                </Button>
              </>
            </Box>
          </Box>
        </Box>

        <TwoFA />
      </Box>
    </div>
  );
};

export default ChangePassword;
